import { getOptionalNumber } from '~/utils/valueParsers';

import type { RoomRateRoom } from '../RoomRatesStep/utils';
import { normaliseRoomName } from '../utils';

type SalesforceRoomAssetOccupancySource = {
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  maximum_occupancy_number__c?: string | number | null;
  name?: string | null;
};

type GetOccupancyPayloadByRoomTypeIdParams = {
  roomAssets: Array<SalesforceRoomAssetOccupancySource | null | undefined>;
  rooms: Array<RoomRateRoom>;
  selectedRoomTypeIds: Array<string>;
};

export type RoomRateOccupancyPayload = {
  include_infants: boolean;
  max_adult_occupancy: number;
  max_child_occupancy: number;
  max_infant_occupancy: number;
  max_occupancy: number;
  max_teenager_occupancy: number;
};

export type CapacityCombination = [adults: number, children: number, infants: number, teenagers: number];

export function getRoomRateCapacityCombinations(
  occupancyPayload: RoomRateOccupancyPayload,
): Array<CapacityCombination> {
  const max = occupancyPayload.max_occupancy;
  const adults = occupancyPayload.max_adult_occupancy;
  const children = occupancyPayload.max_child_occupancy;
  const infants = occupancyPayload.max_infant_occupancy;
  const teenagers = occupancyPayload.max_teenager_occupancy;
  const includeInfants = occupancyPayload.include_infants;
  const total = adults + children + infants + teenagers;
  const limit = Math.min(max, total);

  if (limit === 0 || total === 0) {
    return [];
  }

  const result: Array<CapacityCombination> = [];
  const maxAdults = Math.min(adults, limit);

  for (let a = 1; a <= maxAdults; a++) {
    const maxChildren = Math.min(children, limit - a);

    for (let c = 0; c <= maxChildren; c++) {
      const maxTeenagers = Math.min(teenagers, limit - a - c);

      for (let t = 0; t <= maxTeenagers; t++) {
        const left = limit - (a + c + t);

        if (includeInfants) {
          const maxInfants = Math.min(infants, left);

          for (let i = 0; i <= maxInfants; i++) {
            if (a + c + i + t === limit) {
              result.push([a, c, i, t]);
            }
          }
        } else {
          const i = infants;
          if (a + c + i + t === limit + infants) {
            result.push([a, c, i, t]);
          }
        }
      }
    }
  }

  return result;
}

export function getRoomRateOccupancyPayloadByRoomTypeId({
  roomAssets,
  rooms,
  selectedRoomTypeIds,
}: GetOccupancyPayloadByRoomTypeIdParams) {
  const selectedRoomTypeIdSet = new Set(selectedRoomTypeIds);
  const occupancyByAssetName = new Map<string, RoomRateOccupancyPayload>();

  roomAssets.forEach((roomAsset) => {
    const roomName = normaliseRoomName(roomAsset?.name);

    if (!roomName || occupancyByAssetName.has(roomName)) {
      return;
    }

    const maxAdultOccupancy = getOptionalNumber(roomAsset?.included_number_of_adults__c);
    const maxChildOccupancy = getOptionalNumber(roomAsset?.included_number_of_children__c);
    const maxInfantOccupancy = getOptionalNumber(roomAsset?.included_number_of_infant__c);
    const maxOccupancy = getOptionalNumber(roomAsset?.maximum_occupancy_number__c);
    const maxTeenagerOccupancy = getOptionalNumber(roomAsset?.included_number_of_teenagers__c);

    if (
      maxAdultOccupancy === undefined ||
      maxChildOccupancy === undefined ||
      maxInfantOccupancy === undefined ||
      maxOccupancy === undefined ||
      maxTeenagerOccupancy === undefined
    ) {
      return;
    }

    occupancyByAssetName.set(roomName, {
      include_infants: true,
      max_adult_occupancy: maxAdultOccupancy,
      max_child_occupancy: maxChildOccupancy,
      max_infant_occupancy: maxInfantOccupancy,
      max_occupancy: maxOccupancy,
      max_teenager_occupancy: maxTeenagerOccupancy,
    });
  });

  return new Map(
    rooms
      .filter((room) => selectedRoomTypeIdSet.has(room.id))
      .flatMap((room) => {
        const occupancyPayload = occupancyByAssetName.get(normaliseRoomName(room.name));
        return occupancyPayload ? [[room.id, occupancyPayload] as [string, RoomRateOccupancyPayload]] : [];
      }),
  );
}
